import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useSettings } from '../contexts/SettingsContext';
import { recordReportVisit } from '../utils/reportPreferences';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  AccessTime,
  Schedule,
  Assessment,
  Logout,
  People,
  Business,
  Campaign,
  Security,
  History,
  Person,
  NetworkCheck,
  ChevronLeft,
  ChevronRight,
  Settings,
  EventBusy,
  Notifications,
  Computer,
  FactCheck,
  EmojiEvents,
} from '@mui/icons-material';
import { bonusPortalAPI, bonusTrackerAPI } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import versionInfo from '../config/version.json';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { sidebarCollapsed, toggleSidebar } = useSidebar();
  const { timeOffRequestsEnabled } = useSettings();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (!user?.id) return;
    if (location.pathname.startsWith('/reports/') && location.pathname !== '/reports') {
      recordReportVisit(user.id, location.pathname);
    }
  }, [location.pathname, user?.id]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    toggleSidebar();
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  // Role-based access control
  const hasRole = (requiredRoles: string[]) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  // Operations President may be allowed by TOGO email even if TEKS role is not supervisor+.
  const [togoBonusAccess, setTogoBonusAccess] = useState(false);
  useEffect(() => {
    if (!user || user.role === 'agent') return;
    bonusPortalAPI
      .me()
      .then((me: { allowed?: boolean }) => setTogoBonusAccess(Boolean(me?.allowed)))
      .catch(() => setTogoBonusAccess(false));
  }, [user]);

  // Bonus tracker: supervisors and above, plus approvers named in Settings (accounting, CEO). The API decides.
  const [bonusTrackerAccess, setBonusTrackerAccess] = useState(false);
  useEffect(() => {
    if (!user || user.role === 'agent') {
      setBonusTrackerAccess(false);
      return;
    }
    bonusTrackerAPI
      .me()
      .then((me: { allowed?: boolean }) => setBonusTrackerAccess(Boolean(me?.allowed)))
      .catch(() => setBonusTrackerAccess(false));
  }, [user]);

  const isSupervisorOrAbove = hasRole(['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']);
  const isManagerOrAbove = hasRole(['manager', 'administrator', 'super_user']);
  const isAdministratorOrAbove = hasRole(['administrator', 'super_user']);
  /** TEKS TIME: supervisor and above may enter payroll bonuses (data lives in TOGO OPS). */
  const isBonusPortalRole = hasRole(['supervisor', 'manager', 'administrator', 'super_user']);
  const canSeeBonuses = isBonusPortalRole || togoBonusAccess;
  const hasPermission = (permission: string) => (user?.permissions || []).includes(permission);
  const canViewOperations = hasPermission('operations.attendance_coverage.read') &&
    hasRole(['team_lead', 'supervisor', 'manager', 'administrator', 'super_user', 'hr']);

  // Core functionality for all users
  const coreMenuItems = [
    ...(user?.role === 'agent' 
      ? [{ text: 'Home', icon: <Dashboard />, path: '/home' }]
      : [{ text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' }]
    ),
    ...(hasRole(['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']) ? 
      [{ text: 'Live Status', icon: <People />, path: '/dashboard/live-status' }] : []),
    { text: 'Clock In/Out', icon: <AccessTime />, path: '/clock' },
    { text: 'My Schedule', icon: <Schedule />, path: '/my-schedule' },
    ...(timeOffRequestsEnabled ? 
      [{ text: 'Time Off Requests', icon: <EventBusy />, path: '/time-absence-requests' }] : []),
  ];


  // Reports and analytics
  const reportsMenuItems = [
    { text: 'Time Reports', icon: <Assessment />, path: '/reports' },
    ...(hasPermission('time_entries.read') && hasRole(['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'])
      ? [{ text: 'Time Records', icon: <History />, path: '/management/time-records' }]
      : []),
    ...(hasPermission('payroll.review.read') || hasPermission('reports.read')
      ? [{ text: 'Payroll Review', icon: <AccessTime />, path: '/reports/payroll-review' }]
      : []),
  ];

  // Operations
  const operationsMenuItems = [
    // TOGO OPS bonus tracking — visible for TEKS supervisor+ (and TOGO-allowed Operations President).
    // One bonus entry: the Bonus Tracker. The TEKS Solutions bonus is its second tab (the Operations President may only have that).
    ...(bonusTrackerAccess || canSeeBonuses ? [{ text: 'Bonus Tracker', icon: <EmojiEvents />, path: '/bonus-tracker' }] : []),
    ...(canViewOperations ? [
      { text: 'Daily Attendance & Coverage', icon: <FactCheck />, path: '/operations/attendance-coverage' },
    ] : []),
    ...(hasPermission('users.password_recovery.read') &&
      hasRole(['team_lead', 'supervisor', 'manager', 'administrator', 'super_user', 'hr']) ? [
      { text: 'Password Recovery', icon: <Security />, path: '/operations/password-recovery' },
    ] : []),
    ...(hasPermission('monitoring.console.read') ? [
      { text: 'Workforce Monitoring', icon: <FactCheck />, path: '/workforce-monitoring/overview' },
    ] : []),
    ...(hasPermission('call_qa.read') || hasPermission('call_qa.calls.read') ? [
      { text: 'Call QA', icon: <Assessment />, path: '/call-qa/overview' },
    ] : []),
    // Supervisors+ have schedules.read / schedules.update
    ...(isSupervisorOrAbove ? [
      { text: 'Schedule Management', icon: <Schedule />, path: '/management/schedules' },
    ] : []),
  ];

  // User and organization management
  // Supervisors can see these based on their permissions:
  // - Users: supervisors have users.read (read-only access)
  // - Roles & Permissions: Only administrators and above (supervisors have no settings permissions)
  const userManagementItems = isSupervisorOrAbove ? [
    // Supervisors have users.read permission
    { text: 'Users', icon: <People />, path: '/management/users' },
    // Roles & Permissions is only for administrators and above
    ...(isAdministratorOrAbove ? [{ text: 'Roles & Permissions', icon: <Security />, path: '/management/roles' }] : []),
  ] : [];

  // Organization structure management (Manager and above only)
  const organizationItems = isManagerOrAbove ? [
    { text: 'Departments', icon: <Business />, path: '/management/departments' },
    { text: 'Campaigns', icon: <Campaign />, path: '/management/campaigns' },
    // Only a super user creates the bonus categories that supervisors pick from.
    ...(user?.role === 'super_user' ? [{ text: 'Bonus Categories', icon: <EmojiEvents />, path: '/management/bonus-categories' }] : []),
  ] : [];

  // System settings
  const systemItems = [
    ...(isAdministratorOrAbove ? [
      { text: 'System Settings', icon: <Settings />, path: '/settings' },
      { text: 'Notifications', icon: <Notifications />, path: '/settings/notifications' },
      { text: 'Network Configuration', icon: <NetworkCheck />, path: '/system/network-config' },
      { text: 'Device Restrictions', icon: <Computer />, path: '/system/device-restrictions' },
      { text: 'Alert System', icon: <Notifications />, path: '/system/alert-system' },
      { text: 'Activity Log', icon: <History />, path: '/system/activity-log' }
    ] : []),
    ...(isManagerOrAbove && !isAdministratorOrAbove ? [
      { text: 'Notifications', icon: <Notifications />, path: '/settings/notifications' },
    ] : []),
  ];

  const allMenuItems = [
    ...coreMenuItems,
    ...operationsMenuItems,
    ...reportsMenuItems,
    ...userManagementItems,
    ...organizationItems,
    ...systemItems
  ];

  const drawer = (
    <div>
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: sidebarCollapsed ? 'center' : 'space-between',
        px: sidebarCollapsed ? 1 : 2,
        minHeight: 64
      }}>
        {!sidebarCollapsed && (
          <img 
            src="/logo.svg" 
            alt="TEKS TIME Logo" 
            style={{ height: 32, width: 'auto' }}
          />
        )}
        {sidebarCollapsed && (
          <img 
            src="/logo1.svg" 
            alt="TEKS TIME Logo" 
            style={{ height: 24, width: 'auto' }}
          />
        )}
        <IconButton
          onClick={handleSidebarToggle}
          sx={{ 
            display: { xs: 'none', sm: 'flex' },
            color: sidebarCollapsed ? 'text.secondary' : 'text.secondary',
            ml: sidebarCollapsed ? 0 : 1,
            minWidth: sidebarCollapsed ? 32 : 40,
            minHeight: sidebarCollapsed ? 32 : 40,
            backgroundColor: sidebarCollapsed ? 'transparent' : 'transparent',
            border: 'none',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'action.hover',
              color: 'text.primary'
            }
          }}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft />}
        </IconButton>
      </Toolbar>
      <Divider />
      
      {/* Core Functionality */}
      {!sidebarCollapsed && (
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Core
        </Typography>
      )}
      <List>
        {coreMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                px: sidebarCollapsed ? 1 : 2,
                minHeight: 48
              }}
              title={sidebarCollapsed ? item.text : undefined}
            >
              <ListItemIcon sx={{ 
                minWidth: sidebarCollapsed ? 'auto' : 40,
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {operationsMenuItems.length > 0 && (
        <>
          <Divider />
          {!sidebarCollapsed && (
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              Operations
            </Typography>
          )}
          <List>
            {operationsMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    px: sidebarCollapsed ? 1 : 2,
                    minHeight: 48
                  }}
                  title={sidebarCollapsed ? item.text : undefined}
                >
                  <ListItemIcon sx={{
                    minWidth: sidebarCollapsed ? 'auto' : 40,
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={item.text} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Divider />
      
      {/* Reports & Analytics */}
      {!sidebarCollapsed && (
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Reports & Analytics
        </Typography>
      )}
      <List>
        {reportsMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                px: sidebarCollapsed ? 1 : 2,
                minHeight: 48
              }}
              title={sidebarCollapsed ? item.text : undefined}
            >
              <ListItemIcon sx={{ 
                minWidth: sidebarCollapsed ? 'auto' : 40,
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {userManagementItems.length > 0 && (
        <>
          <Divider />
          
          {/* User Management */}
          {!sidebarCollapsed && (
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              User Management
            </Typography>
          )}
          <List>
            {userManagementItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    px: sidebarCollapsed ? 1 : 2,
                    minHeight: 48
                  }}
                  title={sidebarCollapsed ? item.text : undefined}
                >
                  <ListItemIcon sx={{ 
                    minWidth: sidebarCollapsed ? 'auto' : 40,
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={item.text} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {organizationItems.length > 0 && (
        <>
          <Divider />
          
          {/* Organization */}
          {!sidebarCollapsed && (
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              Organization
            </Typography>
          )}
          <List>
            {organizationItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    px: sidebarCollapsed ? 1 : 2,
                    minHeight: 48
                  }}
                  title={sidebarCollapsed ? item.text : undefined}
                >
                  <ListItemIcon sx={{ 
                    minWidth: sidebarCollapsed ? 'auto' : 40,
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={item.text} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {systemItems.length > 0 && (
        <>
          <Divider />
          
          {/* System */}
          {!sidebarCollapsed && (
            <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              System
            </Typography>
          )}
          <List>
            {systemItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    px: sidebarCollapsed ? 1 : 2,
                    minHeight: 48
                  }}
                  title={sidebarCollapsed ? item.text : undefined}
                >
                  <ListItemIcon sx={{ 
                    minWidth: sidebarCollapsed ? 'auto' : 40,
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && <ListItemText primary={item.text} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { sm: `${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <img 
              src="/logo1.svg" 
              alt="TEKS TIME Logo" 
              style={{ height: 28, width: 'auto' }}
            />
            <Typography variant="h6" noWrap component="div">
              {allMenuItems.find(item => item.path === location.pathname)?.text
                || (location.pathname.startsWith('/workforce-monitoring') ? 'Workforce Monitoring' : '')
                || (location.pathname.startsWith('/call-qa') ? 'Call QA' : '')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ 
          width: { sm: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth }, 
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease'
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
              borderRight: '1px solid #e0e0e0',
              backgroundColor: 'background.paper'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          transition: 'width 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: '100%' }}>
          {children}
        </Box>
        {/* Version Footer */}
        <Box
          sx={{
            mt: 'auto',
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {(() => {
              // Format version based on user role
              // Administrator and above: v1.0.38 (full version)
              // Below administrator: v1.0 (short version)
              const isAdminOrAbove = user?.role === 'administrator' || user?.role === 'super_user';
              const versionParts = versionInfo.version.split('.');
              const displayVersion = isAdminOrAbove 
                ? `v${versionInfo.version}` 
                : `v${versionParts[0]}.${versionParts[1]}`;
              
              // Parse date string and create date in local timezone to avoid UTC conversion issues
              const [year, month, day] = versionInfo.releaseDate.split('-').map(Number);
              const releaseDate = new Date(year, month - 1, day);
              const formattedDate = releaseDate.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              });
              
              return `TEKS TIME ${displayVersion} | Last Updated: ${formattedDate} | © ${new Date().getFullYear()} Teks Solutions LLC`;
            })()}
          </Typography>
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          navigate('/profile');
          handleProfileMenuClose();
        }}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
        {hasRole(['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']) && (
          <MenuItem onClick={() => {
            navigate('/notifications');
            handleProfileMenuClose();
          }}>
            <ListItemIcon>
              <Notifications fontSize="small" />
            </ListItemIcon>
            Notification Preferences
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
